<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { ctaBannerDeposit } from '~/data/pages/banks/mocks'
import type { IResponseData } from '~/types'
import { WidgetAdsBlock } from '~/components/widget/banks-page'
import type { ICredit } from '~/components/entity/credit/item'
import { WidgetAboutPageHeader, mapItemToSchemaData } from '~/components/widget/about-page'
import { FeatureCreditFormRequest } from '~/components/feature/credit/form-request'
import { FeatureCreditCalculator } from '~/components/feature/credit/calculator'
import { ContentBlock } from '~/components/ui/content-block'
import {
  WidgetCreditPageSpecificationsItems,
  WidgetCreditPageInfoItems,
  WidgetCreditPageHeaderSkeleton,
  WidgetCreditPagePurposes,
  WidgetCreditPageRatesTable
} from '~/components/widget/credit-page'
import { WidgetOrganizationAsideContacts } from '~/components/widget/organization-aside-contacts'
import { useSeoFromEntity } from '~/composables/useSeoFromEntity'
import { WidgetSelectCreditType } from '~/components/widget/select-credit-type'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { WidgetRequestFormLink } from '~/components/widget/request-form-link'
import { WidgetRelatedProducts } from '~/components/widget/related-products'
import { Disclaimer } from '~/components/ui/disclaimer'
import { WidgetRating } from '~/components/widget/rating'
import { WidgetDiscussionsAskSpecialist } from '~/components/widget/discussions'

const { t, locale } = useI18n()
const route = useRoute()

const organizationType = computed(() => {
  if (route.params.bank) return 'bank'
  if (route.params.microcredit) return 'microcredit'
  if (route.path.includes('/microcredit/')) return 'microcredit'
  if (route.path.includes('/bank/')) return 'bank'
  return 'bank'
})

const organizationSlug = computed(() => {
  return (route.params.bank || route.params.microcredit) as string
})

const isMicrocredit = computed(() => organizationType.value === 'microcredit')

const creditId = computed(() => {
  const creditParam = route.params.credit as string
  return isMicrocredit.value ? creditParam : parseInt(creditParam)
})

const creditUrl = `/credits/${creditId.value}`
const { data, status } = await useApiWith404<IResponseData<ICredit>>(() => creditUrl, {
  key: `credit-${route.params.credit}`
})

const credit = computed(() => data.value?.data)

const config = useRuntimeConfig()
const baseUrl = config.public.siteUrl
useSeoFromEntity(credit, baseUrl, locale.value)

// Islamic products use "financing" wording instead of "credit"
const isIslamicCredit = computed(() => !!credit.value?.is_islamic)

const breadcrumbs = computed(() => {
  const orgSlug = organizationSlug.value
  const orgTitle = credit.value?.organization.title

  if (isMicrocredit.value) {
    return [
      { title: t('common.microcredits'), url: '/microcredit' },
      {
        title: orgTitle || t('page.credits.detail.breadcrumbs_microcredit_organization'),
        url: orgSlug ? `/microcredit/${orgSlug}` : '/microcredit'
      },
      {
        title: isIslamicCredit.value
          ? t('page.credits.detail.breadcrumbs_financing')
          : t('common.credits'),
        url: orgSlug ? `/microcredit/${orgSlug}/credits` : '/credits'
      },
      {
        title: credit.value?.title || t('page.credits.detail.breadcrumbs_credit'),
        url: credit.value?.url
      }
    ]
  }

  return [
    { title: t('common.banks_of_kyrgyzstan'), url: '/banks' },
    {
      title: orgTitle || t('page.bank.slug.breadcrumbs_default'),
      url: orgSlug ? `/bank/${orgSlug}` : '/banks'
    },
    {
      title: isIslamicCredit.value
        ? t('page.credits.detail.breadcrumbs_financing')
        : t('common.credits'),
      url: orgSlug ? `/bank/${orgSlug}/credits` : '/credits'
    },
    {
      title: credit.value?.title || t('page.credits.detail.breadcrumbs_credit'),
      url: credit.value?.url
    }
  ]
})

const expectedSlug = organizationSlug.value
if (
  credit.value &&
  credit.value.organization?.slug &&
  expectedSlug &&
  credit.value.organization.slug !== expectedSlug
) {
  showError({ statusCode: 404 })
}

const fitItem = computed(() => mapItemToSchemaData(credit.value, t('product.inactive_info')))

const hasCreditInfo = computed(() => {
  return Boolean(
    credit.value?.consideration_period ||
    credit.value?.borrowers_age ||
    credit.value?.borrowers_age_max ||
    credit.value?.borrowers_total_work_experience ||
    credit.value?.borrowers_recent_work_experience
  )
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts
              v-if="credit"
              :organization="credit.organization"
              :type="isMicrocredit ? 'microcredit' : undefined"
            />
            <WidgetRequestFormLink
              v-if="
                credit &&
                credit.rates &&
                credit.organization.permissions.is_credit_request_allowed &&
                credit.organization.status === 'active' &&
                credit.status === 'active'
              "
              :title="$t('page.credits.detail.request_form_title')"
              :description="$t('page.credits.detail.request_form_description')"
              :button="{
                label: $t('page.credits.detail.request_form_button'),
                link: '#lead-request-form'
              }"
              :message="{
                text: $t('page.credits.detail.request_form_message'),
                icon: 'timer-reset'
              }"
            />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <WidgetSelectCreditType :query-params="{ 'filter[position]': 'sidebar' }" />
            <WidgetDiscussionsAskSpecialist />
          </aside>
        </template>
        <div>
          <div class="space-y-5" role="main">
            <WidgetAboutPageHeader v-if="credit" type="credit" :product="fitItem">
              <WidgetCreditPageInfoItems v-if="credit" :credit="credit" />
            </WidgetAboutPageHeader>
            <WidgetCreditPageHeaderSkeleton v-else />

            <WidgetReviveAd zoneid="40" class="overflow-hidden rounded-lg" />

            <WidgetAdsBlock
              v-if="credit && credit.organization.permissions.is_credit_request_allowed"
              :heading="$t(ctaBannerDeposit.heading)"
              :text="$t(ctaBannerDeposit.text)"
              :url="ctaBannerDeposit.url"
              :type="ctaBannerDeposit.type"
            />

            <section>
              <ContentBlock variant="white">
                <Content>
                  <div v-hydrated-html="credit?.description" />
                </Content>
                <WidgetCreditPagePurposes v-if="credit" :credit="credit" />
                <Heading tag="h2" class="h4">{{
                  $t('page.credits.detail.conditions_heading')
                }}</Heading>
                <Content>
                  <div v-hydrated-html="credit?.features" />
                </Content>
                <Heading tag="h2" class="h4">{{
                  $t('page.credits.detail.features_heading')
                }}</Heading>
                <WidgetCreditPageSpecificationsItems v-if="credit" :credit="credit" />
              </ContentBlock>
            </section>

            <section v-if="credit && credit.collateral_insurance">
              <ContentBlock
                variant="white"
                :heading-text="$t('page.credits.detail.collateral_insurance_heading')"
                heading-class="h4"
                heading-tag="h2"
              >
                <Content>
                  <div v-hydrated-html="credit.collateral_insurance" />
                </Content>
              </ContentBlock>
            </section>

            <section v-if="credit && credit.penalties_policy">
              <ContentBlock
                variant="white"
                :heading-text="$t('page.credits.detail.penalties_heading')"
                heading-class="h4"
                heading-tag="h2"
              >
                <Content>
                  <div v-hydrated-html="credit.penalties_policy" />
                </Content>
              </ContentBlock>
            </section>

            <section v-if="credit && credit.initial_fee_description">
              <ContentBlock
                variant="white"
                :heading-text="$t('page.credits.detail.initial_fee_heading')"
                heading-class="h4"
                heading-tag="h2"
              >
                <Content>
                  <div v-hydrated-html="credit.initial_fee_description" />
                </Content>
              </ContentBlock>
            </section>

            <section v-if="credit && credit.commission_fee_description">
              <ContentBlock
                variant="white"
                :heading-text="$t('page.credits.detail.commission_fee_heading')"
                heading-class="h4"
                heading-tag="h2"
              >
                <Content>
                  <div v-hydrated-html="credit.commission_fee_description" />
                </Content>
              </ContentBlock>
            </section>

            <section v-if="hasCreditInfo">
              <ContentBlock variant="white">
                <table class="w-full">
                  <tbody>
                    <tr v-if="credit?.consideration_period" class="border-b leading-4">
                      <td class="py-4 text-gray-500">
                        {{ $t('page.credits.detail.consideration_period') }}
                      </td>
                      <td class="py-4">
                        {{ credit?.consideration_period }}
                        {{ $t('page.credits.detail.days_short') }}
                      </td>
                    </tr>
                    <tr v-if="credit?.borrowers_age" class="border-b leading-4">
                      <td class="py-4 text-gray-500">
                        {{ $t('page.credits.detail.min_borrower_age') }}
                      </td>
                      <td class="py-4">{{ credit?.borrowers_age }}</td>
                    </tr>
                    <tr v-if="credit?.borrowers_age_max" class="border-b leading-4">
                      <td class="py-4 text-gray-500">
                        {{ $t('page.credits.detail.max_borrower_age') }}
                      </td>
                      <td class="py-4">{{ credit?.borrowers_age_max }}</td>
                    </tr>
                    <tr v-if="credit?.borrowers_total_work_experience" class="border-b leading-4">
                      <td class="py-4 text-gray-500">
                        {{ $t('page.credits.detail.total_work_experience') }}
                      </td>
                      <td class="py-4">{{ credit?.borrowers_total_work_experience }}</td>
                    </tr>
                    <tr v-if="credit?.borrowers_recent_work_experience" class="border-b leading-4">
                      <td class="py-4 text-gray-500">
                        {{ $t('page.credits.detail.recent_work_experience') }}
                      </td>
                      <td class="py-4">{{ credit?.borrowers_recent_work_experience }}</td>
                    </tr>
                  </tbody>
                </table>
              </ContentBlock>
            </section>

            <section v-if="credit && credit.rates">
              <ContentBlock
                variant="white"
                :heading-text="`${credit.title} / ${$t('page.credits.detail.rates_heading')}`"
                heading-class="h4"
                heading-tag="h2"
              >
                <WidgetCreditPageRatesTable :credit="credit" />
              </ContentBlock>
            </section>

            <section v-if="credit && credit.rates && !credit.is_islamic">
              <FeatureCreditCalculator
                :credit="credit"
                heading-class="h4"
                heading-tag="h2"
                :heading-text="$t('page.credits.detail.calculator_heading')"
                variant="white"
              />
            </section>
            <WidgetRating
              v-if="credit && config.public.widgetRatingEnabled"
              :entity="credit"
              entity-type="credit"
              :heading-text="
                $t(
                  isIslamicCredit
                    ? 'page.credit.rating_heading_islamic'
                    : 'page.credit.rating_heading',
                  { title: credit.title }
                )
              "
              :be-first-text="
                isIslamicCredit ? $t('widget.rating.stats.be_first_credit_islamic') : ''
              "
            />
            <Disclaimer
              :title="$t('common.product.info_disclaimer.title')"
              :description="$t('common.product.info_disclaimer.description')"
            />
            <div
              v-if="
                credit &&
                credit.rates &&
                credit.organization.permissions.is_credit_request_allowed &&
                credit.organization.status === 'active' &&
                credit.status === 'active'
              "
            >
              <FeatureCreditFormRequest
                :credit="credit"
                :organization="credit.organization"
                :available-currencies="credit.currencies"
                :heading-text="
                  $t(
                    isIslamicCredit
                      ? 'page.credits.detail.form_heading_islamic'
                      : 'page.credits.detail.form_heading',
                    { title: credit.title }
                  )
                "
                heading-class="h4"
                heading-tag="h2"
                variant="white"
              />
            </div>
            <WidgetRelatedProducts
              v-if="credit?.related_products && credit?.related_products.length"
              :products="credit.related_products"
              :status="status"
            />
          </div>
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>

<style scoped>
html {
  scroll-behavior: smooth;
}
</style>
